import { ZipFileSystem, type FileSystem } from '@playcanvas/splat-transform';

import { encodeInstances } from './doc-instances';
import type { EditorSplatResource } from './editor-splat-resource';
import type { Events } from './events';
import type { Scene } from './scene';
import type { Splat } from './splat';
import { writeResourceFile } from './splat-serialize';

// Group layers by the static resource they share and work out, per resource,
// which rows are still referenced. The saved file stores each resource's
// gaussian data once, so a duplicated layer costs its instance list rather than
// a second copy of the scene.
//
// The union across layers is a correctness requirement, not a size
// optimisation: if one layer deleted rows another still references, dropping
// them would corrupt that other layer. Rows nothing references are dropped, so
// deletions become permanent at save - as they already were.
const groupByResource = (splats: Splat[]) => {
    const groups: { resource: EditorSplatResource, layers: Splat[] }[] = [];
    const index = new Map<EditorSplatResource, number>();
    for (const splat of splats) {
        let at = index.get(splat.resource);
        if (at === undefined) {
            at = groups.length;
            index.set(splat.resource, at);
            groups.push({ resource: splat.resource, layers: [] });
        }
        groups[at].layers.push(splat);
    }

    return groups.map(({ resource, layers }) => {
        const referenced = new Uint8Array(resource.numRows);
        for (const layer of layers) {
            const { sourceRow, count } = layer.instances;
            for (let i = 0; i < count; ++i) {
                referenced[sourceRow[i]] = 1;
            }
        }

        // ascending, so the written order is the retained subsequence of the
        // resource's own row order - which is morton order, and what the
        // instance run encoding stays compact under
        let numRows = 0;
        for (let row = 0; row < referenced.length; ++row) {
            if (referenced[row]) numRows++;
        }
        const rows = new Uint32Array(numRows);
        const rowMap = new Uint32Array(resource.numRows);
        let at = 0;
        for (let row = 0; row < referenced.length; ++row) {
            if (referenced[row]) {
                rows[at] = row;
                rowMap[row] = at;
                at++;
            }
        }
        return { resource, layers, rows, rowMap };
    });
};

/** Native save uses lossless PLY; JSI export can convert/reduce saved resources. */
export const serializeEditorProject = async (scene: Scene, events: Events, fs: FileSystem,
    options?: { format?: 'ply' | 'sog', maxSHBands?: number, iterations?: number }): Promise<void> => {
    const splats = events.invoke('scene.allSplats') as Splat[];
    const groups = groupByResource(splats);
    const browserWriter = await fs.createWriter('scene.ssproj');
    const zipFs = new ZipFileSystem(browserWriter);
    const resourceNames = groups.map((_group, index) => `resource_${index}.${options?.format ?? 'ply'}`);
    // Conversion may reorder static rows. Remap every sharing layer's records
    // into the saved order, including hidden layers, without touching live data.
    for (let i = 0; i < groups.length; ++i) {
        const group = groups[i];
        const order = await writeResourceFile(group.resource, group.rows, resourceNames[i], zipFs, options);
        for (let row = 0; row < order.length; row++) group.rowMap[group.rows[order[row]]] = row;
    }

    // layer -> the resource file it reads from, and its remapped records
    const layerInfo = new Map<Splat, { resource: number, records: ArrayBuffer }>();
    groups.forEach((group, resourceIndex) => {
        for (const layer of group.layers) {
            // remap into the compacted row numbering on a copy: the live
            // instance list must keep working after the save
            const records = encodeInstances(layer, group.rowMap);
            layerInfo.set(layer, { resource: resourceIndex, records });
        }
    });

    const document = {
        version: 1,
        camera: scene.camera.docSerialize(),
        view: events.invoke('docSerialize.view'),
        poseSets: events.invoke('docSerialize.poseSets'),
        annotations: events.invoke('docSerialize.annotations'),
        spatialGuides: events.invoke('docSerialize.spatialGuides'),
        jsiSceneData: await events.invoke('docSerialize.jsiSceneData'),
        timeline: events.invoke('docSerialize.timeline'),
        resources: groups.map((group, i) => ({
            filename: resourceNames[i],
            numRows: group.rows.length
        })),
        splats: splats.map((splat, i) => ({
            ...splat.docSerialize(),
            resource: layerInfo.get(splat).resource,
            instances: `instances_${i}.bin`
        }))
    };

    // Write document.json
    const docWriter = await zipFs.createWriter('document.json');
    await docWriter.write(new TextEncoder().encode(JSON.stringify(document)));
    await docWriter.close();

    // Write each layer's instance list and palettes
    for (let i = 0; i < splats.length; ++i) {
        const writer = await zipFs.createWriter(`instances_${i}.bin`);
        await writer.write(new Uint8Array(layerInfo.get(splats[i]).records));
        await writer.close();
    }

    // Close zip (also closes underlying browser writer)
    await zipFs.close();
};
